// The geometry of the week grid: how a time becomes a pixel offset.
//
// Shared by the grid, the drag ghost and the now-indicator, all of which have
// to agree — a lesson drawn with one constant and a drop target computed with
// another lands the event an hour from where it was released.
//
// ── The whole day, opening at 7:00 (#749) ───────────────────────────────────
//
// As in MMS, Week and Day are the whole 24 hours in a scroller, opened with
// 7:00 AM at the top (its default "Initial Display Time"; no setting hides any
// hours). So every offset here is from midnight, and where the grid opens is
// the core's (`gridOpenHour`, `OPEN_HOUR` in core/src/week.rs), shared with
// both phones' Week grids.

import { gridOpenHour } from '../../domain/calendarGrid';

export const HOURS = Array.from({ length: 24 }, (_, i) => i);
export const DAY_MINUTES = 24 * 60;
// px per hour. The height gives the five-minute drop step
// (domain/calendarDrop.js) a little over 10px to land in.
export const HOUR_HEIGHT = 128;
export const SNAP_MINUTES = 15;
export const PIXELS_PER_MINUTE = HOUR_HEIGHT / 60;
export const GRID_HEIGHT = 24 * HOUR_HEIGHT;
// The time-label gutter, which the now-indicator has to clear.
export const GUTTER_PX = 52;

// How far a pointer must travel before a press counts as a drag rather than a
// click. Below it, short gestures fall through to click-to-create.
export const DRAG_THRESHOLD_PX = 4;
export const MIN_DRAG_DURATION_MIN = 15;

// A lesson's start, as a `Date` (#269). Every value handed here is a `Date`:
// `lesson.date`, which `useCalendarOccurrences.js` builds as
// `new Date(`${o.date}T${o.startTime || '00:00'}`)`, or `NowIndicator.jsx`'s
// `now`. Nothing in `src/` writes a Firestore `Timestamp` into a calendar row,
// so there is no `.toDate()` case. If a document instant ever reaches a row,
// this is the one function that has to learn about it.
export const asDate = (value) => new Date(value);

export function getTopOffset(date) {
  const d = asDate(date);
  const minutes = d.getHours() * 60 + d.getMinutes();
  return minutes * PIXELS_PER_MINUTE;
}

// Where the grid's scroller opens: the core's hour at the top.
export function openScrollTop() {
  return gridOpenHour() * HOUR_HEIGHT;
}

export function getHeight(durationMin) {
  return (durationMin || 60) * PIXELS_PER_MINUTE;
}

// Pointer position to a minute of the day, snapped to SNAP_MINUTES. Shift
// disables snapping for fine-grained drags. The column's rectangle is the
// whole day's, scrolled or not, so its top is midnight wherever it is on the
// screen; a pointer past either end is held at that end.
export function pointerToMinute(clientY, colEl, shiftKey) {
  const rect = colEl.getBoundingClientRect();
  const rawMin = Math.min(DAY_MINUTES, ((clientY - rect.top) / HOUR_HEIGHT) * 60);
  if (shiftKey) return Math.max(0, Math.round(rawMin));
  return Math.max(0, Math.floor(rawMin / SNAP_MINUTES) * SNAP_MINUTES);
}

// A minute of the day on `day`, as a `Date`. A click on the grid's last
// quarter-hour is 23:45; the end of a drag to the bottom edge is 24:00, which
// is never a start.
export function minuteToDate(day, minute) {
  const d = new Date(day);
  d.setHours(Math.floor(minute / 60), minute % 60, 0, 0);
  return d;
}

export function formatDuration(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return `${Math.floor(minutes / 60)}h${rest ? ` ${rest}min` : ''}`;
}
